/**
 * Inline dislike reason panel.
 *
 * Multi-select reasons (filtered by target type + module) plus one optional
 * free-text field. On submit it persists the dislike and reports back whether
 * the server flagged the human-review offer.
 *
 * @module DislikePanel
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FREE_TEXT_MAX, reasonsFor } from '@/lib/ratingConstants';
import type { RatingModule, RatingTargetType } from '@/lib/ratingConstants';

export interface DislikePanelProps {
  readonly targetType: RatingTargetType;
  readonly module: RatingModule;
  readonly initialReasonCodes?: string[];
  readonly initialFreeText?: string;
  readonly isSaving: boolean;
  readonly onSubmit: (reasonCodes: string[], freeText: string) => void;
  readonly onCancel: () => void;
}

export function DislikePanel({
  targetType,
  module,
  initialReasonCodes = [],
  initialFreeText = '',
  isSaving,
  onSubmit,
  onCancel,
}: DislikePanelProps) {
  const { t } = useTranslation();
  const options = reasonsFor(targetType, module);
  const [selected, setSelected] = useState<Set<string>>(new Set(initialReasonCodes));
  const [freeText, setFreeText] = useState(initialFreeText);
  const [error, setError] = useState(false);

  const toggle = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
    setError(false);
  };

  const handleSubmit = () => {
    if (selected.size === 0) {
      setError(true);
      return;
    }
    onSubmit(Array.from(selected), freeText.trim());
  };

  return (
    <div className="mt-2 space-y-3 rounded-lg border border-border bg-muted/30 p-3">
      <p className="font-ui text-xs font-semibold text-foreground">
        {t('rating.dislikePanelTitle')}
      </p>

      <fieldset className="space-y-2">
        <legend className="sr-only">{t('rating.dislikePanelTitle')}</legend>
        {options.map((option) => {
          const id = `reason-${targetType}-${option.code}`;
          return (
            <div key={option.code} className="flex items-start gap-2">
              <Checkbox
                id={id}
                checked={selected.has(option.code)}
                onCheckedChange={() => toggle(option.code)}
              />
              <Label htmlFor={id} className="font-ui cursor-pointer text-xs font-normal leading-snug">
                {t(option.labelKey)}
              </Label>
            </div>
          );
        })}
      </fieldset>

      <div className="space-y-1.5">
        <Textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value.slice(0, FREE_TEXT_MAX))}
          placeholder={t('rating.freeTextPlaceholder')}
          maxLength={FREE_TEXT_MAX}
          rows={2}
          aria-label={t('rating.freeTextPlaceholder')}
        />
      </div>

      {error && (
        <p className="font-ui text-xs text-destructive" role="alert">
          {t('rating.selectReason')}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button type="button" size="sm" onClick={handleSubmit} disabled={isSaving}>
          {isSaving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
          {isSaving ? t('rating.saving') : t('rating.submit')}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={isSaving}>
          {t('rating.cancel')}
        </Button>
      </div>
    </div>
  );
}
